import { normalisePricingUnitKey } from '../utils/pricing-unit-key.js';

export const CATALOGUE_CATEGORIES = ['materials', 'labour', 'equipment', 'fixed', 'packages', 'subcontractors'];
export const taxBasisLabel = basis => basis === 'ex_gst' ? 'ex GST' : basis === 'inc_gst' ? 'inc GST' : 'GST basis unrecorded';
const normal = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

export function labourTradeKey(name, contractorName = '') {
    let trade = normal(name);
    if (trade === 'water proofer') trade = 'waterproofer';
    if (trade === 'brick layer') trade = 'bricklayer';
    if (trade === 'tiler (wall and floor)') trade = 'tiler';
    return `${encodeURIComponent(trade)}|${encodeURIComponent(normal(contractorName))}`;
}

export function labourBasis(unit) {
    const key = normalisePricingUnitKey(unit);
    return key === 'per_day' ? 'day' : key === 'per_hour' ? 'hour' : null;
}

export function groupLabourRates(rows) {
    const groups = new Map();
    for (const row of rows) {
        const basis = labourBasis(row.unit);
        if (!basis) continue;
        const name = row.name || row.trade || row.item;
        const key = labourTradeKey(name, row.contractorName);
        if (!groups.has(key)) groups.set(key, { key, name, contractorName: row.contractorName || '', day: [], hour: [] });
        groups.get(key)[basis].push(row);
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));
}

// A preference can resolve an unspecified basis, never convert quantities or
// replace an explicit hourly/day input. Same-basis conflicts stay ambiguous.
export function preferredLabourCandidates(rows, trade, requestedUnit, preferences = {}) {
    if (normalisePricingUnitKey(requestedUnit)) return rows;
    const key = labourTradeKey(trade);
    const matchingTrade = rows.filter(row => labourTradeKey(row.trade || row.name, row.contractorName) === key);
    const saved = [...new Set(matchingTrade.map(row => row.preferredBasis ?? row.preferred_basis).filter(Boolean))];
    const preference = saved.length === 1 ? saved[0] : saved.length > 1 ? null : Object.hasOwn(preferences, key) ? preferences[key] : null;
    if (!['day', 'hour'].includes(preference)) return rows;
    const matching = rows.filter(row => labourTradeKey(row.trade || row.name, row.contractorName) === key && labourBasis(row.unit) === preference);
    return matching.length ? matching : rows;
}

// Filing is a view of stable catalogue identities. It does not rewrite source
// arrays or invalidate existing quote references. Explicit operator filing wins.
export function catalogueCategory(row, fallback) {
    const override = row.catalogueCategory ?? row.catalogue_category;
    if (CATALOGUE_CATEGORIES.includes(override)) return override;
    if (fallback === 'subcontractors') return fallback;
    const name = normal(row.name || row.item || row.trade);
    const unit = normal(row.unit);
    if (/\b(registration fee|contract preparation|engineering|engineer report|srd|clearance certificate|council permits|mgnmt plans|management plans|flood test|plumbers dye)\b/.test(name)
        || (fallback === 'labour' && /^per (project|quote|inspection)$/.test(unit))) return 'fixed';
    if (/\b(hire|skip bins?|rubbish removal|boom lift|scissor lift|cherry picker|cheery picker|scaffold|generator|pressure cleaner|roof safety rails)\b/.test(name)) return 'equipment';
    if (/^bricks \(laying\)$|^brick repointing$|^concrete grinding$|^premium floor finishing/.test(name)) return 'labour';
    if (/supply[, ]+(?:and )?(?:install|lay)|supply, install/.test(name)
        || /^(excavations|rendering|painting \((internal|external)\)|water proofing \((torch on|pvc)\)|engineered tile screed|cement tile screed|air conditioner \(relocation fee\)|glass replacement|dri zoro treatment|injectable dpc|concrete resurfacing)/.test(name)) return 'packages';
    return fallback;
}
